import { Theme } from '@mui/material';

/**
 * ============================================================
 * THEME OVERRIDE CONTRACT (DO NOT VIOLATE)
 * ============================================================
 *
 * 1. MUI palette:
 *    - Semantic colors only (text.primary, background.paper, etc.)
 *    - Must use concrete color values (hex / rgb)
 *    - NEVER reference CSS variables
 *
 * 2. Theme overrides (this file):
 *    - Structural defaults only
 *    - Remove MUI behaviors (indicators, shadows, casing)
 *    - No page-specific visuals
 *    - No layout-driven UX
 *
 * 3. CssBaseline:
 *    - Defines CSS variables based on palette.mode
 *    - Variables are consumed ONLY by CSS / SCSS
 *
 * 4. Page-level SCSS:
 *    - Owns layout, tabs, headers, surfaces
 *    - Owns light/dark visual differences
 *    - Owns media queries
 *
 * HARD RULE:
 * ❌ Never use `var(--*)` inside MUI color props
 * ❌ Never style Tabs / Headers here
 *
 * If it changes per page or per breakpoint, it does NOT belong here.
 */

export const overrides = (theme: Theme): any => {
  const mode = (theme as Theme & { customMode?: string }).customMode || theme.palette.mode;

  return {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          subtitle1: 'h6',
          subtitle2: 'h6',
          body: 'p',
          body1: 'p',
          body2: 'p',
          body3: 'p',
          body4: 'p',
          body5: 'p',
          body6: 'p',
          body7: 'p',
          textVariant_32_500: 'h1',
          textVariant_24_500: 'h2',
          textVariant_16_500: 'h3',
          textVariant_16_400: 'p',
          textVariant_12_400: 'span',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        color: 'primary', // 🚨 THIS IS CRITICAL
      },
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          padding: '0.7rem 1.5rem',
          borderRadius: '0.3rem',
          fontSize: '1.2rem',
          fontWeight: 700,
          lineHeight: '1.6rem',
          height: '3rem',
          textTransform: 'none',
          boxShadow: 'none !important',
          [theme.breakpoints.down('lg')]: {
            ['@media (orientation: landscape)']: {
              fontSize: '1.5rem',
              lineHeight: '2rem',
              height: '3.5rem',
              padding: '0.75rem 1.35rem',
            },
          },
          [theme.breakpoints.down('md')]: {
            fontSize: '1.5rem',
            lineHeight: '2rem',
            height: '3.5rem',
            padding: '0.75rem 1.35rem',
          },
        },
        containedPrimary: {
          backgroundColor: theme.palette.primary.main,
          color: '#FFF',
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: '#FFF',
            opacity: 0.85,
          },
          '&.Mui-disabled': {
            backgroundColor: mode === 'calm' ? '#A6B9CF' : mode === 'nature' ? '#9DB8A7' : '#979797',
            color: '#FFF',
          },
        },
        outlined: {
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-disabled': {
            borderColor: '#96969699',
          },
        },
        outlinedPrimary: {
          background: 'none',
          border: `1px solid ${theme.palette.primary.main}`,
          text: {
            color: theme.palette.primary.main,
          },
        },
        text: {
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '1.2rem',
          lineHeight: '1.6rem',
          '&:hover': {
            color: `${theme.palette.primary.main} !important`,
          },
        },
        textPrimary: {
          color: theme.palette.primary.main,
          '&:hover': {
            color: '#FFF',
          },
          '&.Mui-disabled': {
            color: '#FFF',
            opacity: 0.5,
          },
        },
        textSecondary: {
          color: '#626363',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          color: theme.palette.text.primary,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 3,
          padding: '5px 0',
        },
        thumb: {
          width: 11,
          height: 11,
        },
        track: {
          border: 'none',
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: 999,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: '#626363',
          [theme.breakpoints.down('md')]: {
            right: '1.2rem',
            width: '1.2rem !important',
          },
        },
        select: {
          minHeight: 'auto',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          paddingTop: '1.2rem',
          paddingBottom: '1.2rem',
          color: '#000',
          fontFamily: 'Open Sans',
          fontSize: '1.7rem',
          fontStyle: 'normal',
          fontWeight: 400,
          '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
        },
        root: {
          '&:before': {
            borderBottom: '1px solid #FFF',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-error:before': {
            borderBottom: '1px solid #F52F2F',
          },
          '&::placeholder': {
            color: theme.palette.text.secondary,
          },
        },
        input: {
          color: theme.palette.text.primary,
          fontSize: '1.4rem',
          lineHeight: '1.9rem',
          padding: '0.55rem 1.5rem',
          backgroundColor: theme.palette.form.main,
          '&::placeholder': {
            color: theme.palette.text.secondary,
            fontSize: '1.4rem',
            [theme.breakpoints.down('lg')]: {
              ['@media (orientation: landscape)']: {
                fontSize: '1.5rem',
              },
            },
            [theme.breakpoints.down('md')]: {
              fontSize: '1.5rem',
            },
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: theme.palette.text.secondary,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.form.disabled,
          },
          [theme.breakpoints.down('lg')]: {
            ['@media (orientation: landscape)']: {
              fontSize: '1.5rem',
              lineHeight: '2rem',
              padding: '7.5px 15px',
            },
          },
          [theme.breakpoints.down('md')]: {
            fontSize: '1.5rem',
            lineHeight: '2rem',
            padding: '7.5px 15px',
          },
        },
        adornedStart: {
          paddingLeft: '14px',
        },
        disabled: {},
        [theme.breakpoints.down('md')]: {
          root: {
            '&.Mui-error:before': {
              borderBottom: '1px solid #F52F2F',
            },
            '&::placeholder': {
              color: '#D1D1D1',
            },
          },
          input: {
            fontSize: '1.5rem',
            lineHeight: '2rem',
            padding: '7.5px 15px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          lineHeight: '1.6rem',
          borderRadius: '3px',
          backgroundColor: theme.palette.form.main,
          '&.Mui-disabled': {
            backgroundColor: theme.palette.form.disabled,
          },
          '&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.form.outline,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.form.outline,
            borderWidth: 1,
          },
        },
        multiline: {
          padding: 0,
          height: 70,
          alignItems: 'flex-start',
        },
        input: {
          height: 'inherit',
          fontWeight: 500,
          padding: '0.55rem 1.5rem',
          borderRadius: '3px',
          ...(theme.palette.mode === 'dark' && { caretColor: theme.palette.primary.main }),
          '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
        },
        inputMultiline: {
          height: 'calc(100% - 2rem) !important',
          fontSize: '1.4rem',
          lineHeight: '1.6rem',
          padding: '1rem 1.5rem',
          ...(theme.palette.mode === 'dark' && { caretColor: theme.palette.primary.main }),
          '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
            fontSize: '1.4rem',
          },
        },
        inputAdornedStart: {
          paddingLeft: 0,
          marginLeft: -8,
        },
        notchedOutline: {
          borderRadius: '3px',
          borderColor: theme.palette.form.outline,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: '#000',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '1.5rem',
          lineHeight: '2rem',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'var(--app-dialog-shadow)',
        },
        paperFullScreen: {
          backgroundColor: 'var(--app-page-bg)',
          color: theme.palette.text.primary,
        },
      },
    },
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiGrid2: {
      defaultProps: {
        disableEqualOverflow: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
          boxShadow: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          minHeight: 46,
          padding: 0,

          display: 'flex',
          alignItems: 'center',
        }),

        /* ✅ Critical: removes extra vertical inflation */
        content: {
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },

        title: ({ theme }: { theme: Theme }) => ({
          fontSize: '1.4rem',
          fontWeight: 700,
          lineHeight: '1.9rem',
          textTransform: 'uppercase',
          color: theme.palette.text.primary,
        }),

        /* ✅ Tighten spacing for PatientQueue subtitle */
        subheader: ({ theme }: { theme: Theme }) => ({
          fontSize: '1.1rem',
          lineHeight: '1.4rem',
          marginTop: 2,
          padding: 0,
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '5px 20px',

          '&:last-child': {
            paddingBottom: '5px',
          },
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        paper: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'break-spaces',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.05)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'break-spaces',
          padding: '10px 0',
          margin: 0,
        },
        gutters: {
          padding: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        dense: {
          padding: 0,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'initial',
          marginRight: '1rem',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 20px 15px 20px',
        },
      },
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          right: '0px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
        },
        primary: {
          fontWeight: 400,
          fontSize: '1.4rem',
          lineHeight: '1.9rem',
          letterSpacing: '-0.336px',
        },
        secondary: {
          fontWeight: 700,
          fontSize: '1.1rem',
          lineHeight: '1.5rem',
          letterSpacing: '-0.336px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          overflowY: 'none',
          padding: 20,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          overflowY: 'none',
          padding: '0 20px 20px',
          '&>:not(:first-of-type)': {
            marginLeft: 10,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },

    // NOTE:
    // Tabs visuals (colors, backgrounds, selected state)
    // are controlled at the page SCSS level.
    // This override exists only to disable default MUI behavior.

    MuiTabs: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          minHeight: 46,
          backgroundColor: 'var(--app-tabs-bg)',
        }),

        indicator: {
          display: 'none',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: () => ({
          minWidth: 119,
          minHeight: 46,
          padding: '0 20px',
          fontSize: 16,
          fontWeight: 500,
          color: 'var(--app-tab-text)',
          backgroundColor: 'transparent',
          transition: 'color 160ms ease, background-color 160ms ease',

          '&.Mui-selected': {
            fontWeight: 700,
            color: 'var(--app-brand-primary)',
            backgroundColor: 'var(--app-surface)',
          },
        }),
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#626363',
          background: theme.palette.mode === 'dark' ? '#2A2F35' : '#F3F3F3',
          width: 'max-content',
          maxWidth: 280,
          padding: '4px 8px',
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.4rem',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.12)' : 'none',
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 20px rgba(0,0,0,0.35)' : 'none',
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.35)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          width: '28px',
          height: '16px',
          padding: 0,
          display: 'flex',

          '&:active .MuiSwitch-thumb': {
            width: 12,
            [theme.breakpoints.down('md')]: {
              width: '1.6rem',
            },
          },

          [theme.breakpoints.down('md')]: {
            width: '4.1rem',
            height: '2.4rem',
          },
        }),

        switchBase: ({ theme }: { theme: Theme }) => ({
          padding: 2,

          '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: '#fff',

            '& + .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: theme.palette.primary.main,
            },

            [theme.breakpoints.down('md')]: {
              transform: 'translateX(1.6rem)',
            },
          },

          [theme.breakpoints.down('md')]: {
            padding: '0.3rem',
          },
        }),

        thumb: ({ theme }: { theme: Theme }) => ({
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          width: 12,
          height: 12,
          borderRadius: 6,

          [theme.breakpoints.down('md')]: {
            width: '1.8rem',
            height: '1.8rem',
            borderRadius: '50%',
          },
        }),

        track: ({ theme }: { theme: Theme }) => ({
          borderRadius: 16 / 2,
          opacity: 1,
          boxSizing: 'border-box',

          backgroundColor:
            theme.palette.mode === 'light'
              ? '#E6E6E6' // lighter, correct light-mode track
              : '#4A4F55', // darker charcoal for dark mode

          [theme.breakpoints.down('md')]: {
            borderRadius: '1.2rem',
          },
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorDisabled: {
          color: theme.palette.text.primary,
        },
      },
    },
  };
};
